import { useState, useEffect, useRef, useCallback } from "react";
import { firestore, collection, query, where, onSnapshot } from "../firebase";

/**
 * Subscribes to the current user's grocery items in Firestore.
 * Returns [items, setItems, { registerPendingAdd }]. When user is null, items are [].
 * Only items registered as "pending add" are kept when missing from a snapshot;
 * items missing because they were deleted elsewhere are removed.
 */
export function useUserItems(user) {
  const [items, setItems] = useState([]);
  const setItemsRef = useRef(setItems);
  setItemsRef.current = setItems;
  const pendingAddIdsRef = useRef(new Set());

  const registerPendingAdd = useCallback((id) => {
    if (id) pendingAddIdsRef.current.add(id);
  }, []);

  useEffect(() => {
    if (!user) {
      setItems([]);
      pendingAddIdsRef.current.clear();
      return;
    }

    const q = query(
      collection(firestore, "items"),
      where("userId", "==", user.uid)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const serverItems = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        const serverIds = new Set(serverItems.map((i) => i.id));
        serverIds.forEach((id) => pendingAddIdsRef.current.delete(id));

        setItemsRef.current((prev) => {
          const merged = [...serverItems];
          const pending = prev.filter(
            (i) => i.id && !serverIds.has(i.id) && pendingAddIdsRef.current.has(i.id)
          );
          return [...merged, ...pending];
        });
      },
      (error) => {
        console.warn("Grocery items:", error);
        setItemsRef.current([]);
      }
    );

    return () => unsubscribe();
  }, [user]);

  return [items, setItems, { registerPendingAdd }];
}
